import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, AbstractControl, FormControl } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { SettingsService } from 'src/app/views/settings/settings.service';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';

@Component({
  selector: 'app-service-purchase-order-preview',
  templateUrl: './service-purchase-order-preview.component.html',
  styleUrls: ['./service-purchase-order-preview.component.css']
})
export class ServicePurchaseOrderPreviewComponent implements OnInit {

  // Section expand/collapse flags
  isForm1Expand: boolean = true;
  isForm2Expand: boolean = true;
  isForm3Expand: boolean = true;
  isForm4Expand: boolean = true;
  isForm5Expand: boolean = true;
  isForm6Expand: boolean = true;
  isForm7Expand: boolean = true;
  isForm8Expand: boolean = true;
  isForm9Expand: boolean = true;
  isForm10Expand: boolean = true;

  // Route params
  serviceRfqId!: number;
  quoteId!: number;
  servicePurchaseOrderId!: number;
  currentTab: string = 'preview';

  // Form groups
  costSummaryFormGroup!: UntypedFormGroup;
  headerFormGroup!: UntypedFormGroup;
  paymentFormGroup!: UntypedFormGroup;
  lineItemTableForm!: UntypedFormGroup;

  // Data
  dataSource: any[] = [];
  headerResponse: any = null;
  uploadedAdditionalAttachmentsArray: any[] = [];

  // Carousel
  previewSection: boolean = false;
  previewDoc: any[] = [];
  currentIndex = 0;

  // Dialogs
  showCloseDialogue: boolean = false;

  tabs: any[] = [
    { index: 0, label: 'LINE ITEMS', icon: 'directions_boat', route: 'line-items' },
    { index: 1, label: 'HEADER DETAILS', icon: 'looks', route: 'header' },
    { index: 2, label: 'PAYMENT TERMS', icon: 'location_searching', route: 'payment-terms' },
    { index: 3, label: 'PREVIEW', icon: 'location_searching', route: 'preview' }
  ];

  constructor(
    private snack: MatSnackBar,
    private _formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private cookieService: CookieService,
    private router: Router,
    private SettingsService: SettingsService,
    private fullLoaderService: FullLoaderService,
    private activatedRoute: ActivatedRoute
  ) {}

  get rowControls(): AbstractControl[] {
    return (this.lineItemTableForm.get('rows') as UntypedFormArray).controls;
  }

  getRowControl(rowIndex: number, field: string): FormControl {
    return this.rowControls[rowIndex].get(field) as FormControl;
  }

  async ngOnInit() {
    this.fullLoaderService.showLoader();

    // Initialize form groups
    this.costSummaryFormGroup = this._formBuilder.group({
      lineItemListPriceSubtotal: [0],
      lineItemDiscount: [0],
      lineItemSubtotal: [0],
      generalDiscountType: [''],
      generalDiscountValue: [0],
      lineItemApplicableTaxProfile: [''],
      lineItemTaxAmount: [0],
      quoteLineItemFreight: [[]],
      lineItemFreightTotal: [0],
      lineItemTotal: [0],
      currency: [null]
    });

    this.headerFormGroup = this._formBuilder.group({
      // PO Details
      purchaseOrderRefNumber: [''],
      purchaseOrderDate: [''],
      rfqRefNumber: [''],
      rfqDate: [''],
      quoteRefNumber: [''],
      quotationDate: [''],
      expectedDeliveryDate: [''],
      // Buyer Company
      buyerCompanyName: [''],
      buyerEmail: [''],
      buyerPhone: [''],
      buyerFax: [''],
      buyerAddress: [''],
      // Buyer Contact
      buyerContactPerson: [''],
      buyerContactEmail: [''],
      buyerContactPhone: [''],
      buyerJobTitle: [''],
      buyerContactFax: [''],
      buyerContactAddress: [''],
      // Supplier
      supplierContactName: [''],
      supplierEmail: [''],
      supplierPhone: [''],
      supplierFax: [''],
      supplierCompanyEmail: [''],
      supplierCompanyName: [''],
      supplierAddress: [''],
      // Vessel
      vesselName: [''],
      vesselIMONumber: [''],
      vesselType: [''],
      vesselClass: [''],
      // Port
      portName: [''],
      portCode: [''],
      vesselETA: [''],
      vesselETD: [''],
      vesselEtaTime: [''],
      vesselEtdTime: ['']
    });

    this.paymentFormGroup = this._formBuilder.group({
      // Buyer Payment Terms
      billingAddress: [''],
      applicableTaxProfile: [''],
      buyersPaymentTerms: [''],
      buyerTermsAndConditions: [''],
      // Port Agent
      portAgencyName: [''],
      portAgentName: [''],
      portAgentPhoneNumber: [''],
      portAgentContact: [''],
      portAgentPhoneFax: [''],
      portAgentEmail: [''],
      portAgentAddress: [''],
      // Freight Forwarder
      freightCompanyName: [''],
      freightPIC: [''],
      freightJobTitle: [''],
      freightEmail: [''],
      freightPhone: [''],
      freightFax: [''],
      freightAddress: [''],
      freightPackaging: [''],
      freightTransportMode: [''],
      // T&C
      termsAndConditions: ['']
    });

    this.lineItemTableForm = this._formBuilder.group({
      rows: this._formBuilder.array([])
    });

    const params = this.activatedRoute.snapshot.queryParams;

    if (params['serviceRfqId'] && params['quoteId'] && params['servicePurchaseOrderId']) {
      this.serviceRfqId = params['serviceRfqId'];
      this.quoteId = params['quoteId'];
      this.servicePurchaseOrderId = params['servicePurchaseOrderId'];

      // Fetch all 3 sections in parallel
      const [lineItemsResult, headerResult, paymentTermsResult] = await Promise.all([
        this.SettingsService.fetchServicePurchaseOrder({
          purchaseOrderId: this.servicePurchaseOrderId,
          section: 'lineItems'
        }),
        this.SettingsService.fetchServicePurchaseOrder({
          purchaseOrderId: this.servicePurchaseOrderId,
          section: 'header'
        }),
        this.SettingsService.fetchServicePurchaseOrder({
          purchaseOrderId: this.servicePurchaseOrderId,
          section: 'paymentTerms'
        })
      ]);

      // Populate line items + cost summary
      if (lineItemsResult && lineItemsResult.length > 0) {
        const lineData = lineItemsResult[0];
        this.populateLineItems(lineData);
      }

      // Populate header
      if (headerResult && headerResult.length > 0) {
        const header = headerResult[0];
        this.headerResponse = header;
        this.populateHeader(header);
      }

      // Populate payment terms
      if (paymentTermsResult && paymentTermsResult.length > 0) {
        const payment = paymentTermsResult[0];
        this.populatePaymentTerms(payment);
      }
    }

    this.fullLoaderService.hideLoader();
  }

  populateLineItems(lineData: any) {
    // Cost summary
    let currency = lineData.currency;
    if (typeof currency === 'string') {
      try { currency = JSON.parse(currency); } catch (e) { currency = null; }
    }

    let freight = lineData.quoteLineItemFreight;
    if (typeof freight === 'string') {
      try { freight = JSON.parse(freight); } catch (e) { freight = []; }
    }

    this.costSummaryFormGroup.patchValue({
      lineItemListPriceSubtotal: lineData.lineItemListPriceSubtotal || 0,
      lineItemDiscount: lineData.lineItemDiscount || 0,
      lineItemSubtotal: lineData.lineItemSubtotal || 0,
      generalDiscountType: lineData.generalDiscountType || '',
      generalDiscountValue: lineData.generalDiscountValue || 0,
      lineItemApplicableTaxProfile: lineData.lineItemApplicableTaxProfile || '',
      lineItemTaxAmount: lineData.lineItemTaxAmount || 0,
      quoteLineItemFreight: freight || [],
      lineItemFreightTotal: lineData.lineItemFreightTotal || 0,
      lineItemTotal: lineData.lineItemTotal || 0,
      currency: currency
    });

    // Line items table
    let quotationLineItems = lineData.quotationLineItems;
    if (typeof quotationLineItems === 'string') {
      try { quotationLineItems = JSON.parse(quotationLineItems); } catch (e) { quotationLineItems = []; }
    }

    if (quotationLineItems && quotationLineItems.length > 0) {
      this.dataSource = quotationLineItems;
      this.populateForm(quotationLineItems);
    }
  }

  populateForm(data: any[]) {
    const rows = this.lineItemTableForm.get('rows') as UntypedFormArray;
    rows.clear();

    data.forEach((item: any, index: number) => {
      rows.push(this._formBuilder.group({
        position: [index + 1],
        serviceName: [item.serviceName || ''],
        serviceDescription: [item.serviceDescription || ''],
        buyerComment: [item.buyerComment || ''],
        supplierComment: [item.supplierComment || ''],
        uom: [item.uom || ''],
        supplierUom: [item.supplierUom || ''],
        quantity: [item.quantity || 0],
        supplierQuantity: [item.supplierQuantity || 0],
        unitCost: [item.unitCost || 0],
        discount: [item.discount || 0],
        discountedUnitPrice: [item.discountedUnitPrice || 0],
        totalCost: [item.totalCost || 0],
        leadTime: [item.leadTime || ''],
        isActive: [item.isActive]
      }));
    });
  }

  populateHeader(header: any) {
    this.headerFormGroup.patchValue({
      purchaseOrderRefNumber: header.purchaseOrderRefNumber,
      purchaseOrderDate: header.purchaseOrderDate,
      rfqRefNumber: header.rfqRefNumber,
      rfqDate: header.rfqDate,
      quoteRefNumber: header.quoteRefNumber,
      quotationDate: header.quotationDate,
      expectedDeliveryDate: header.expectedDeliveryDate,
      buyerCompanyName: header.buyerCompanyName,
      buyerEmail: header.buyerEmail,
      buyerPhone: header.buyerPhone,
      buyerFax: header.companyFax,
      buyerAddress: header.companyAddress,
      buyerContactPerson: header.buyerContactPerson,
      buyerContactEmail: header.buyerEmail,
      buyerContactPhone: header.buyerPhone,
      buyerJobTitle: header.buyerJobTitle,
      buyerContactFax: header.buyerFax,
      buyerContactAddress: header.buyerAddress,
      supplierContactName: header.supplierName,
      supplierEmail: header.supplierEmail,
      supplierPhone: header.supplierPhone,
      supplierFax: header.supplierFax,
      supplierCompanyEmail: header.supplierCompanyEmail,
      supplierCompanyName: header.supplierCompanyName,
      supplierAddress: header.supplierAddress,
      vesselName: header.vesselName,
      vesselIMONumber: header.vesselImoNumber,
      vesselType: header.vesselType,
      vesselClass: header.class,
      portName: header.portName,
      portCode: header.portCode,
      vesselETA: header.vesselEta,
      vesselETD: header.vesselEtd,
      vesselEtaTime: header.vesselEtaTime,
      vesselEtdTime: header.vesselEtdTime
    });
  }

  populatePaymentTerms(payment: any) {
    this.paymentFormGroup.patchValue({
      billingAddress: payment.billingAddress,
      applicableTaxProfile: payment.applicableTaxProfile,
      buyersPaymentTerms: payment.buyersPaymentTerms,
      buyerTermsAndConditions: payment.termsAndConditions,
      portAgencyName: payment.portAgencyName,
      portAgentName: payment.portAgentName,
      portAgentPhoneNumber: payment.portAgentPhoneNumber,
      portAgentContact: payment.portAgentContact,
      portAgentPhoneFax: payment.portAgentPhoneFax,
      portAgentEmail: payment.emailId,
      portAgentAddress: payment.address,
      freightCompanyName: payment.freightCompanyName,
      freightPIC: payment.freightPIC,
      freightJobTitle: payment.freightJobTitle,
      freightEmail: payment.freightEmail,
      freightPhone: payment.freightPhone,
      freightFax: payment.freightFax,
      freightAddress: payment.freightAddress,
      freightPackaging: payment.freightPackaging,
      freightTransportMode: payment.freightTransportMode,
      termsAndConditions: payment.termsAndConditions
    });

    let attachments = payment.paymentTermsAttachments;
    if (typeof attachments === 'string') {
      try { attachments = JSON.parse(attachments); } catch (e) { attachments = []; }
    }
    this.uploadedAdditionalAttachmentsArray = attachments || [];
  }

  // Save as draft
  async save(isDraft?: boolean) {
    if (isDraft) {
      this.showCloseDialogue = false;
    }

    this.fullLoaderService.showLoader();

    const params = {
      section: 'preview',
      purchaseOrderStatus: 14,
      purchaseOrderTabId: 4,
      quoteId: this.quoteId,
      serviceRfqId: this.serviceRfqId,
      userId: Number(this.cookieService.get('userId')),
      purchaseOrderId: this.servicePurchaseOrderId,
    };

    await this.SettingsService.updateServicePurchaseOrder(params);

    this.fullLoaderService.hideLoader();
    this.snack.open('Purchase Order saved as draft', 'OK', { duration: 3000 });

    if (isDraft) {
      this.router.navigate(['/operations/buyer-service-quote-list']);
    }
  }

  // Tab navigation
  selectMatTabLink(tabItem: any) {
    const qp = {
      serviceRfqId: this.serviceRfqId,
      quoteId: this.quoteId,
      servicePurchaseOrderId: this.servicePurchaseOrderId
    };

    if (tabItem.route === 'line-items') {
      this.router.navigate(['operations', 'createServiceOrder', 'line-items'], { queryParams: qp });
    } else if (tabItem.route === 'header') {
      this.router.navigate(['operations', 'createServiceOrder', 'header'], { queryParams: qp });
    } else if (tabItem.route === 'payment-terms') {
      this.router.navigate(['operations', 'createServiceOrder', 'payment-terms'], { queryParams: qp });
    }
  }

  // Section toggle
  toggleVisibility(section: string) {
    switch (section) {
      case 'form1': this.isForm1Expand = !this.isForm1Expand; break;
      case 'form2': this.isForm2Expand = !this.isForm2Expand; break;
      case 'form3': this.isForm3Expand = !this.isForm3Expand; break;
      case 'form4': this.isForm4Expand = !this.isForm4Expand; break;
      case 'form5': this.isForm5Expand = !this.isForm5Expand; break;
      case 'form6': this.isForm6Expand = !this.isForm6Expand; break;
      case 'form7': this.isForm7Expand = !this.isForm7Expand; break;
      case 'form8': this.isForm8Expand = !this.isForm8Expand; break;
      case 'form9': this.isForm9Expand = !this.isForm9Expand; break;
      case 'form10': this.isForm10Expand = !this.isForm10Expand; break;
    }
  }

  // Attachment preview carousel
  togglePreview(value: boolean, section: string) {
    this.currentIndex = 0;
    if (value && section === 'generalAttachment') {
      this.previewDoc = this.uploadedAdditionalAttachmentsArray;
    } else {
      this.previewDoc = [];
    }
    this.previewSection = value;
  }

  getHeaderAttachmentCount(): number {
    return this.uploadedAdditionalAttachmentsArray.length;
  }

  nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.previewDoc.length;
  }

  prevSlide() {
    this.currentIndex = (this.currentIndex - 1 + this.previewDoc.length) % this.previewDoc.length;
  }

  isPdf(urlOrDataUrl: string): boolean {
    if (urlOrDataUrl?.toLowerCase().startsWith('data:application/pdf')) {
      return true;
    }
    return urlOrDataUrl?.toLowerCase().endsWith('.pdf');
  }

  // Close / Cancel
  close(isDraft: boolean) {
    if (isDraft) {
      this.showCloseDialogue = true;
    }
  }

  cancel() {
    this.showCloseDialogue = false;
    this.router.navigate(['/operations/buyer-service-quote-list']);
  }

}
